import { getTracker } from './slsTracker'
import { getToken, getUserId } from '@/utils/auth'
import phoneVersion from './phoneVersion'

/**
 * SLS 日志工具类
 * 符合全链路日志规范，统一 key 命名，便于在 SLS 日志平台配置
 */
class SlsLogger {
  constructor () {
    // 生成 traceId（用于全链路追踪）
    this.traceId = this.generateTraceId()
    // 服务名称
    this.serviceName = 'pre-education-m-h5'
  }

  /**
   * 生成 traceId（用于全链路追踪）
   */
  generateTraceId () {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }

  /**
   * 获取基础日志数据（符合全链路日志规范）
   */
  getBaseLogData () {
    const userAgent = navigator.userAgent || ''
    const isIOS = /iPhone|iPad|iPod|Macintosh|Mac OS X/i.test(userAgent)
    const isAndroid = /Android/i.test(userAgent)
    const isWeiXin = phoneVersion.isWeiXin()

    return {
      // 全链路追踪ID（统一使用 trace_id）
      trace_id: this.traceId,
      // 时间戳（统一使用 timestamp，ISO 8601 格式）
      timestamp: new Date().toISOString(),
      // 日志级别（统一使用 log_level）
      log_level: 'info', // 默认 info，会在具体方法中覆盖
      // 服务名称（统一使用 service_name）
      service_name: this.serviceName,
      // 环境信息（统一使用 env）
      env: process.env.VUE_APP_ENV || 'unknown',
      // 用户信息（统一使用 user_id）
      user_id: getUserId() || '',
      has_token: !!getToken(),
      // 设备信息（统一使用 device_info 对象）
      device_info: {
        user_agent: userAgent,
        platform: isIOS ? 'iOS' : (isAndroid ? 'Android' : 'Other'),
        is_weixin: isWeiXin,
        screen_width: window.screen?.width || 0,
        screen_height: window.screen?.height || 0
      },
      // 页面信息（统一使用 page_info 对象）
      page_info: {
        path: window.location.pathname,
        query: window.location.search
      }
    }
  }

  /**
   * 发送日志（核心方法，所有日志都通过此方法发送）
   * @param {string} eventType - 事件类型（如：page_load, api_call, user_action, flow_step, error）
   * @param {string} eventName - 事件名称（可读性好的名称）
   * @param {string} logLevel - 日志级别（info, warn, error）
   * @param {string} status - 状态（success, error, warning, info）
   * @param {string} methodName - 方法名称
   * @param {object} params - 参数数据
   * @param {object} result - 结果数据
   * @param {object} errorInfo - 错误信息（如果有）
   * @param {number} durationMs - 耗时（毫秒，用于 API 调用）
   */
  log (eventType, eventName, logLevel, status, methodName = '', params = {}, result = {}, errorInfo = null, durationMs = null) {
    try {
      const tracker = getTracker()
      if (!tracker) {
        // 如果 tracker 未初始化，只打印到控制台
        console.log('[SLS Log]', { eventType, eventName, logLevel, status, methodName, params, result, errorInfo, durationMs })
        return Promise.resolve()
      }

      // 构建符合全链路日志规范的日志数据
      const logData = {
        ...this.getBaseLogData(),
        // 事件类型（统一使用 event_type）
        event_type: eventType,
        // 事件名称（统一使用 event_name）
        event_name: eventName,
        // 日志级别（统一使用 log_level）
        log_level: logLevel,
        // 状态（统一使用 status）
        status: status,
        // 方法名称（统一使用 method_name）
        method_name: methodName || '',
        // 参数（统一使用 params，已脱敏）
        params: this.sanitizeData(params),
        // 结果（统一使用 result，已脱敏）
        result: this.sanitizeData(result),
        // 错误信息（统一使用 error 对象）
        ...(errorInfo && {
          error: {
            error_msg: errorInfo.message || '',
            error_stack: errorInfo.stack || '',
            error_code: errorInfo.code || ''
          }
        }),
        // 耗时（统一使用 duration_ms，单位：毫秒）
        ...(durationMs !== null && { duration_ms: durationMs })
      }

      // 使用 sendImmediate 立即发送
      const resultPromise = tracker.sendImmediate(logData)

      // 如果返回 Promise，返回它；否则返回 resolved Promise
      if (resultPromise && typeof resultPromise === 'object' && typeof resultPromise.then === 'function') {
        return resultPromise.catch(err => {
          // 日志发送失败不影响业务，只打印警告
          console.warn('SLS 日志发送失败:', err)
          return Promise.resolve()
        })
      }

      return Promise.resolve()
    } catch (error) {
      // 日志记录异常不影响业务，只打印警告
      console.warn('SLS 日志记录异常:', error)
      return Promise.resolve()
    }
  }

  /**
   * 记录页面加载
   */
  logPageLoad (params = {}) {
    try {
      return this.log(
        'page_load',
        '页面加载',
        'info',
        'info',
        'pageLoad',
        params,
        {},
        null,
        null
      )
    } catch (error) {
      console.warn('记录页面加载日志失败:', error)
      return Promise.resolve()
    }
  }

  /**
   * 记录API调用
   * @param {string} apiName - API名称
   * @param {string} status - 状态（info, success, error）
   * @param {object} requestData - 请求数据
   * @param {object} responseData - 响应数据
   * @param {object} errorInfo - 错误信息
   * @param {number} durationMs - 耗时（毫秒）
   */
  logApiCall (apiName, status, requestData = {}, responseData = {}, errorInfo = null, durationMs = null) {
    try {
      const logLevel = status === 'error' ? 'error' : (status === 'success' ? 'info' : 'info')
      return this.log(
        'api_call',
        `API调用: ${apiName}`,
        logLevel,
        status,
        apiName,
        requestData,
        responseData,
        errorInfo,
        durationMs
      )
    } catch (error) {
      console.warn('记录API调用日志失败:', error)
      return Promise.resolve()
    }
  }

  /**
   * 记录用户操作
   */
  logUserAction (actionName, actionData = {}) {
    try {
      return this.log(
        'user_action',
        `用户操作: ${actionName}`,
        'info',
        'info',
        actionName,
        actionData,
        {},
        null,
        null
      )
    } catch (error) {
      console.warn('记录用户操作日志失败:', error)
      return Promise.resolve()
    }
  }

  /**
   * 记录错误
   */
  logError (errorName, errorInfo, contextData = {}) {
    try {
      return this.log(
        'error',
        `错误: ${errorName}`,
        'error',
        'error',
        errorName,
        contextData,
        {},
        errorInfo,
        null
      )
    } catch (error) {
      console.warn('记录错误日志失败:', error)
      return Promise.resolve()
    }
  }

  /**
   * 记录关键流程节点
   */
  logFlowStep (stepName, status, stepData = {}, errorInfo = null) {
    try {
      const logLevel = status === 'error' ? 'error' : (status === 'warning' ? 'warn' : 'info')
      return this.log(
        'flow_step',
        `流程节点: ${stepName}`,
        logLevel,
        status,
        stepName,
        stepData,
        {},
        errorInfo,
        null
      )
    } catch (error) {
      console.warn('记录流程节点日志失败:', error)
      return Promise.resolve()
    }
  }

  /**
   * 数据脱敏处理（移除敏感信息）
   */
  sanitizeData (data) {
    try {
      if (!data || typeof data !== 'object') {
        return data
      }

      const sensitiveKeys = ['password', 'token', 'access_token', 'refreshToken', 'appKey', 'secret', 'key', 'app_id', 'app_key']
      const sanitized = { ...data }

      for (const key in sanitized) {
        if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk.toLowerCase()))) {
          sanitized[key] = '***'
        } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null && !Array.isArray(sanitized[key])) {
          sanitized[key] = this.sanitizeData(sanitized[key])
        }
      }

      return sanitized
    } catch (error) {
      console.warn('数据脱敏处理失败:', error)
      return data
    }
  }
}

// 导出单例
export default new SlsLogger()
